<script setup lang="ts">
import type { LogicalFilter } from '#layers/data-model/public';
import type { FilterSchemaCollection } from './utils/filter-schema';
import { cloneDeep } from 'lodash-es';
import { useEmitAsProps } from 'reka-ui';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isFieldFilter, isLogicalFilter, isLogicalFilterAnd } from '#layers/data-model/public';
import { getFilterList, useLogicalFilter } from '#layers/filters/composables/use-logical-filter';
import { useRelatedItemMatch } from '#layers/filters/composables/use-related-item-match';
import { injectFilterSchemaContext } from './utils/filter-schema-context';
import { useFilterConfigResolver } from './utils/use-filter-config-resolver';

export type ClearFilterDisplayStyle = 'none' | 'button' | 'link';

withDefaults(defineProps<{
	collection: FilterSchemaCollection;
	hideGroupActions?: boolean;
	hideOuterGroup?: boolean;
	disabled?: boolean;
	clearFilterDisplayStyle?: ClearFilterDisplayStyle;
	readonly?: boolean;
	enableVariables?: boolean;
}>(), {
	hideGroupActions: true,
	clearFilterDisplayStyle: 'none',
});

const emits = defineEmits<{
	remove: [];
	duplicate: [];
}>();

const filter = defineModel<LogicalFilter>({ required: true });
const actionEmits = useEmitAsProps(emits);

const { t } = useI18n();
const operator = computed(() => isLogicalFilterAnd(filter.value) ? '_and' : '_or');
const operatorLabel = computed(() => t(`filters.operators.${operator.value}`));

const filterList = computed({
	get() {
		return cloneDeep(getFilterList(filter.value));
	},
	set(value) {
		filter.value = { [operator.value]: value } as LogicalFilter;
	},
});

const { findPrimitiveFieldById, findSingleRelationFieldById } = injectFilterSchemaContext();
const { supportedOperatorsFor, defaultOperatorFor } = useFilterConfigResolver();
const { isRelatedItemMatch } = useRelatedItemMatch();

const { handleAdd, handleAddCondition, handleUpdate, handleRemove, handleDuplicate, handleTurnIntoGroup, handleToggleOperator, handleClearFilter } = useLogicalFilter(filter, {
	findPrimitiveFieldById,
	findSingleRelationFieldById,
	supportedOperatorsFor,
	defaultOperatorFor,
});
</script>

<template>
	<div class="flex flex-col gap-2">
		<FilterHandleGroup v-if="!hideOuterGroup">
			<DButton
				class="px-2 uppercase"
				variant="link"
				color="primary"
				size="sm"
				:disabled
				@click="handleToggleOperator"
			>
				{{ operatorLabel }}
			</DButton>
			<span class="inline-flex flex-1 items-center px-2 text-xs font-medium whitespace-nowrap text-dimmed">{{ $t(`filters.${operator}_text`) }}</span>
			<FilterActions v-if="hideGroupActions" v-bind="actionEmits" :disabled />
		</FilterHandleGroup>
		<div class="flex items-start gap-2">
			<ul class="ml-3 flex flex-1 flex-col gap-2" :class="{ 'border-l pl-3': !hideOuterGroup }">
				<li v-for="(subFilter, index) in filterList" :key="index">
					<FilterRelatedItem
						v-if="isFieldFilter(subFilter) && isRelatedItemMatch(subFilter, collection)"
						hide-group-actions
						:model-value="subFilter"
						:collection
						:disabled
						:readonly
						@update:model-value="handleUpdate(index, $event)"
						@remove="handleRemove(index)"
						@duplicate="handleDuplicate(index)"
						@turn-into-group="handleTurnIntoGroup(index)"
					/>
					<FilterSimple
						v-else-if="isFieldFilter(subFilter)"
						hide-group-actions
						:model-value="subFilter"
						:collection
						:disabled
						:readonly
						:enable-variables
						@update:model-value="handleUpdate(index, $event)"
						@remove="handleRemove(index)"
						@duplicate="handleDuplicate(index)"
						@turn-into-group="handleTurnIntoGroup(index)"
					/>
					<FilterLogicalGroup
						v-else-if="isLogicalFilter(subFilter)"
						:model-value="subFilter"
						:collection
						:disabled
						:readonly
						:enable-variables
						@update:model-value="handleUpdate(index, $event)"
						@remove="handleRemove(index)"
						@duplicate="handleDuplicate(index)"
					/>
				</li>
				<li>
					<div class="flex items-center gap-2">
						<FilterAdd
							v-if="!readonly"
							class="w-full"
							:collection
							:disabled
							:include-group-action="false"
							@add-filter="handleAdd"
							@add-condition="handleAddCondition"
						>
							<template #default>
								<DButton
									color="neutral"
									variant="ghost"
									icon="lucide:plus"
									:aria-label="$t('filters.filter')"
									:label="$t('filters.add_filter')"
									size="sm"
									:disabled
								/>
							</template>
						</FilterAdd>
						<DButton
							v-if="clearFilterDisplayStyle === 'button' && filterList.length > 0"
							:label="t('filters.clear_filter')"
							color="neutral"
							variant="ghost"
							class="text-muted"
							icon="lucide:x"
							size="sm"
							:disabled
							:readonly
							@click="handleClearFilter"
						/>
					</div>
				</li>
			</ul>
		</div>
	</div>
</template>
